import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';

import type { W_WorkOrder_FindPublicConfirmation_VM } from 'ab-api/web/workOrder/findPublicConfirmation';

import { DOWNLOAD_ORDER_CONFIRMATION } from 'af-constants/reduxForms';

import { Button, infoToast } from '@acceligentllc/storybook';

import Loading from 'af-components/DocumentViewer/Loading';

import type { RootState } from 'af-reducers/index';

import * as WorkOrderActions from 'af-actions/workOrder';

import WorkOrderConfirmation from '../Shared/WorkOrderConfirmation';

interface PathParams {
	publicLink: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

type Props = OwnProps & ConnectedProps<typeof connector>;

const ConfirmationPublic: React.FC<Props> = (props) => {
	const {
		isDownloading,
		downloadWorkOrderConfirmationPdfPublic,
		findForConfirmationByPublicLink,
		match: { params: { publicLink } },
	} = props;

	const [workOrder, setWorkOrder] = React.useState<Nullable<W_WorkOrder_FindPublicConfirmation_VM>>(null);

	React.useEffect(() => {
		const initialize = async () => {
			const _workOrder = await findForConfirmationByPublicLink(publicLink);
			setWorkOrder(_workOrder);
		};
		initialize();
		// Disabling cause we only want to do this once
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const downloadPdf = React.useCallback(async () => {
		infoToast(`${publicLink}-confirmation`, { title: 'Started preparing confirmation for download...' });
		await downloadWorkOrderConfirmationPdfPublic(publicLink, `WO_confirmation_${publicLink}.pdf`);
	}, [downloadWorkOrderConfirmationPdfPublic, publicLink]);

	if (!workOrder) {
		return <Loading />;
	}

	return (
		<div className="form-segment form-segment--midi">
			<div className="form-box form-box--full-padded">
				<WorkOrderConfirmation workOrder={workOrder} />
			</div>
			<div className="btn--floating">
				<Button
					icon="UtilitiesPrint"
					onClick={downloadPdf}
					size="big"
					style="primary"
					submitting={isDownloading}
					tooltip="Print"
					type="button"
				/>
			</div>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	return {
		isDownloading: state.http.submitting.includes(DOWNLOAD_ORDER_CONFIRMATION),
	};
}

function mapDispatchToProps() {
	return {
		findForConfirmationByPublicLink: WorkOrderActions.findForConfirmationByPublicLink,
		downloadWorkOrderConfirmationPdfPublic: WorkOrderActions.downloadWorkOrderConfirmationPdfPublic,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(ConfirmationPublic);
